import React, {
  lazy,
  ReactNode,
  Suspense,
} from 'react';

import * as uuid from 'uuid';
import {
  Box,
  Button,
  Snackbar,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Route, Switch, Redirect } from 'react-router-dom';
import { ConnectedRouter } from 'connected-react-router';
import classNames from 'classnames';
import { Alert } from '@material-ui/lab';
import { history } from '../../../core';
import routes from './routes';
import styles from './Main.styles';
import ProgressMask from '../../../components/progressMask';
import PopupDialog from '../../../components/popupDialog';
import { MainDispatchFunc, MessageBoxInfo, SnackbarColor } from './types';
import SettingsPopup from './mainMenu/settingsPopup';

type Props = {
  isMessageBoxOpen: boolean;
  messageBoxInfo?: MessageBoxInfo;
  closeMessageBox: MainDispatchFunc;
  snackbarInfo: {
    isSnackbarOpen: boolean;
    severity?: SnackbarColor;
    messageSnackbar?: string;
  };
  closeSnackBar: MainDispatchFunc;
};

function refineMessageBoxContent(content: string | ReactNode | ReactNode[]) {
  if (typeof content === 'string') {
    return content.split('\n').map((subContent) => (
      <Typography variant="body1" key={uuid.v4()}>
        {subContent}
      </Typography>
    ));
  }

  return content;
}

const Login = lazy(() => import('../login'));
const Result = lazy(() => import('../result'));
const ResultDetails = lazy(() => import('../resultDetails'));
const ResultPreview = lazy(() => import('../resultPreview'));
const useStyles = makeStyles(styles);

const Main: React.FC<Props> = ({
  isMessageBoxOpen,
  messageBoxInfo,
  closeMessageBox,
  snackbarInfo: {
    isSnackbarOpen,
    severity,
    messageSnackbar,
  },
  closeSnackBar,
}: Props) => {
  const classes = useStyles();

  const dismissMessageBox = () => {
    if (messageBoxInfo?.onDismiss) {
      messageBoxInfo?.onDismiss();
    }

    return closeMessageBox();
  };

  return (
    <Box className={classes.root}>
      <ConnectedRouter history={history}>
        <Suspense
          fallback={(
            <Box className={classes.progressContainer}>
              <ProgressMask />
            </Box>
          )}
        >
          <Switch>
            <Route exact path={routes.main}>
              <Redirect to={routes.login} />
            </Route>
            <Route exact path={routes.login} component={Login} />
            <Route path={routes.result} component={Result} />
            <Route path={routes.resultDetails} component={ResultDetails} />
            <Route path={routes.resultPreview} component={ResultPreview} />
          </Switch>
        </Suspense>
      </ConnectedRouter>
      <PopupDialog
        open={messageBoxInfo && isMessageBoxOpen}
        title={messageBoxInfo?.title}
        closeAction={dismissMessageBox}
        classes={{
          backdrop: classNames(
            classes.messageBoxBackdrop,
            messageBoxInfo?.blurBackdrop
              ? classes.messageBoxBackdropBlur
              : undefined,
          ),
        }}
        renderCommandActions={() => (
          <>
            {messageBoxInfo?.commands?.map((command) => (
              <Button
                key={command.title}
                color={command.color}
                classes={{ root: classes.messageBoxButtons }}
                variant="contained"
                size="large"
                onClick={command.action}
              >
                {command.title}
              </Button>
            ))}
            <Button
              color="primary"
              classes={{ root: classes.messageBoxButtons }}
              variant="contained"
              size="large"
              onClick={() => dismissMessageBox()}
            >
              {messageBoxInfo?.dismissText || 'Cancel'}
            </Button>
          </>
        )}
      >
        <Box className={classes.messageBoxContent}>
          {refineMessageBoxContent(messageBoxInfo?.content)}
        </Box>
      </PopupDialog>
      <Snackbar
        open={isSnackbarOpen}
        autoHideDuration={5000}
        onClose={closeSnackBar}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Alert onClose={closeSnackBar} severity={severity}>
          {messageSnackbar}
        </Alert>
      </Snackbar>
      <SettingsPopup />
    </Box>
  );
};

export default Main;
